import React from 'react';
import { Button, Checkbox, Tooltip, Typography } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import PrintIcon from '@mui/icons-material/Print';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Compress, Expand } from '@mui/icons-material';
import {
  ColumnScopedSearchBar,
  SearchableColumn,
} from './datagrid/ColumnScopedSearchBar';

const TFT_SEARCH_COLUMNS: SearchableColumn[] = [
  { value: 'all', label: 'All', type: 'string' },
  { value: 'routeId', label: 'Route Name', type: 'string' },
  { value: 'rutekode', label: 'Rutekode', type: 'string' },
  { value: 'turnummer', label: 'Turnummer', type: 'number' },
  { value: 'togref', label: 'Togref', type: 'string' },
  { value: 'containerId', label: 'ContainerId', type: 'string' },
  {
    value: 'inneholdsbeskrivelse',
    label: 'Inneholdsbeskrivelse',
    type: 'string',
  },
  { value: 'carRegistrationNumber', label: 'Car Reg. Number', type: 'string' },
];

interface CustomGridToolbarProps {
  onExportTFTExcelClicked: () => void;
  printLoadingLists?: {
    onPrint: () => void;
    disabled: boolean;
    loading: boolean;
  };
  toggleRowHeight?: {
    isChecked: boolean;
    onToggleRowHeight: () => void;
  };
  toggleColumnWidth?: {
    isAdjusted: boolean;
    onToggleColumnWidth: () => void;
  };
}

export function TftCustomGridToolbar(props: CustomGridToolbarProps) {
  const {
    onExportTFTExcelClicked,
    printLoadingLists,
    toggleRowHeight,
    toggleColumnWidth,
  } = props;
  const { t } = useTranslation();

  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-start' }}>
      <GridToolbarColumnsButton
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <GridToolbarFilterButton
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <GridToolbarDensitySelector
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <Button
        style={{ color: '#69bc46' }}
        startIcon={<SaveAltIcon />}
        size="small"
        onClick={onExportTFTExcelClicked}
      >
        {t('actions.export', { item: 'filtered excel' })}
      </Button>
      {printLoadingLists ? (
        <Button
          startIcon={<PrintIcon />}
          size="small"
          disabled={printLoadingLists.disabled || printLoadingLists.loading}
          onClick={printLoadingLists.onPrint}
        >
          {t(
            printLoadingLists.loading
              ? 'attributes.preparingLoadingLists'
              : 'attributes.printLoadingLists',
          )}
        </Button>
      ) : null}
      {toggleRowHeight ? (
        <Checkbox
          onChange={toggleRowHeight.onToggleRowHeight}
          checked={toggleRowHeight.isChecked}
          icon={<Expand style={{ color: '#69bc46' }} />}
          checkedIcon={<Compress color="action" />}
          title={
            toggleRowHeight.isChecked
              ? t('attributes.compactRowHeight')
              : t('attributes.dynamicRowHeight')
          }
          inputProps={{
            'aria-label': toggleRowHeight.isChecked
              ? t('attributes.compactRowHeight')
              : t('attributes.dynamicRowHeight'),
          }}
        />
      ) : null}
      {toggleColumnWidth ? (
        <Tooltip
          title={
            toggleColumnWidth.isAdjusted
              ? 'Revert Column Width'
              : 'Adjust Column Width'
          }
        >
          <Checkbox
            size="small"
            checked={toggleColumnWidth.isAdjusted}
            onChange={toggleColumnWidth.onToggleColumnWidth}
            icon={<ViewColumnIcon style={{ color: '#69bc46' }} />}
            checkedIcon={<ViewColumnIcon color="action" />}
            inputProps={{
              'aria-label': toggleColumnWidth.isAdjusted
                ? 'Revert Column Width'
                : 'Adjust Column Width',
            }}
          />
        </Tooltip>
      ) : null}
      <ColumnScopedSearchBar columns={TFT_SEARCH_COLUMNS} />
      <Tooltip title={`${t('attributes.sortingInfo')}`}>
        <Typography style={{ marginLeft: 8, fontSize: 13 }}>
          {`(i)${t('attributes.sortingIconText')}`}
        </Typography>
      </Tooltip>
    </GridToolbarContainer>
  );
}
